import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
  Alert,
  AlertDescription,
  useAuth,
} from '@aaas/shared';
import { FileText, File, FileType, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import type { DriveSyncedFile } from '@aaas/shared';
import { useListDriveSyncedFiles } from '../hooks/useDriveIntegrationQuery';

interface GoogleDriveFilesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  integrationId: string;
  integrationName: string;
}

const GoogleDriveFilesDialog = ({
  isOpen,
  onClose,
  spaceId,
  integrationId,
  integrationName,
}: GoogleDriveFilesDialogProps) => {
  const { idToken } = useAuth();

  // Fetch files list
  const {
    data: filesResponse,
    isLoading: isLoadingFiles,
    error: filesError,
  } = useListDriveSyncedFiles({
    idToken: idToken || undefined,
    spaceId: spaceId || undefined,
    integrationId: integrationId || undefined,
    enabled: isOpen && !!idToken && !!spaceId && !!integrationId,
  });

  const files: DriveSyncedFile[] = (filesResponse?.data as DriveSyncedFile[]) || [];

  // Derived stats
  const totalFiles = files.length;
  const uploadedCount = files.filter((f) => f.has_document_for_process).length;
  const availableCount = files.filter(
    (f) => !f.has_document_for_process && !f.is_deleted_in_provider
  ).length;

  // Utility: Format file size
  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // Utility: Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Utility: Get file icon
  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) {
      return <FileType className="h-5 w-5 text-red-500" />;
    }
    if (mimeType.includes('word') || mimeType.includes('document')) {
      return <FileText className="h-5 w-5 text-blue-500" />;
    }
    if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return <FileText className="h-5 w-5 text-green-600" />;
    }
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) {
      return <FileText className="h-5 w-5 text-orange-500" />;
    }
    if (mimeType.includes('text')) {
      return <File className="h-5 w-5 text-gray-500" />;
    }
    return <FileText className="h-5 w-5 text-gray-400" />;
  };

  // Component: File List Item
  const FileListItem = ({ file }: { file: DriveSyncedFile }) => {
    const isUploaded = file.has_document_for_process;

    return (
      <div
        className={`
          flex items-start gap-3 p-3 border rounded-lg
          ${isUploaded ? 'border-green-200 bg-green-50' : 'bg-white'}
        `}
      >
        {isUploaded && <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />}

        <div className="mt-0.5">{getFileIcon(file.file_mime_type)}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium truncate">{file.file_name}</span>
            {isUploaded && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                Uploaded
              </span>
            )}
          </div>

          <div className="text-xs text-gray-500 mt-1 space-y-0.5">
            {file.file_size && <div>Size: {formatFileSize(file.file_size)}</div>}
            <div>Modified: {formatDate(file.file_modified_time)}</div>
            {file.last_synced_at && (
              <div className="text-green-600">Last synced: {formatDate(file.last_synced_at)}</div>
            )}
            {file.sync_error && <div className="text-red-600">{file.sync_error}</div>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Files from &quot;{integrationName}&quot;</DialogTitle>
          <DialogDescription>
            All files tracked from this connection. Files with green checkmarks have been uploaded
            to your knowledge base.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading State */}
          {isLoadingFiles && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin mb-3" />
              <p className="text-sm text-gray-600">Loading files...</p>
            </div>
          )}

          {/* Error State */}
          {filesError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>Failed to load files: {filesError.message}</AlertDescription>
            </Alert>
          )}

          {/* Files List */}
          {!isLoadingFiles && !filesError && files.length > 0 && (
            <>
              {/* Stats Summary */}
              <div className="flex items-center gap-4 text-sm text-gray-600 pb-2 border-b">
                <span>{totalFiles} total files</span>
                {uploadedCount > 0 && (
                  <>
                    <span>•</span>
                    <span className="text-green-600">{uploadedCount} uploaded</span>
                  </>
                )}
                {availableCount > 0 && (
                  <>
                    <span>•</span>
                    <span>{availableCount} not uploaded</span>
                  </>
                )}
              </div>

              {/* Scrollable file list */}
              <div className="max-h-[400px] overflow-y-auto space-y-2">
                {files.map((file) => (
                  <FileListItem key={file.id} file={file} />
                ))}
              </div>
            </>
          )}

          {/* Empty State */}
          {!isLoadingFiles && !filesError && files.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-gray-300 mb-3" />
              <h3 className="font-semibold text-gray-900">No Files Found</h3>
              <p className="text-sm text-gray-500 mt-1">
                This connection has no tracked files yet.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GoogleDriveFilesDialog;
